import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { clone, forEach, get, isNil, isEmpty } from 'lodash'
import Pluralize from 'pluralize'
import LabeledTextField from '../common/LabeledTextField'
import SelectField from '../common/SelectField'
import LabeledJsonField from '../common/LabeledJsonField'
import JsonField from '../common/JsonField'
import SubmitButton from '../common/SubmitButton'
import typeToPayloadMap from './businessRulePayloads'
import CapitalizeValueDisplay from '../common/CapitalizeValueDisplay'
import MatchingDataFields from './MatchingDataFields'

export default class SharedBusinessRuleForm extends Component {
  static propTypes = {
    businessRuleTypes: PropTypes.array.isRequired,
    matchingTypes: PropTypes.array.isRequired,
    resourceErrors: PropTypes.object,
    resourceErroredFields: PropTypes.array,
    requestInProgress: PropTypes.bool.isRequired,
    initialValues: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    submitLabel: PropTypes.string,
    createValidation: PropTypes.func.isRequired,
    id: PropTypes.number
  }

  constructor(props) {
    super(props)

    this.state = {
      values: this.props.initialValues,
      requestValidated: false,
      validationInProgress: false,
      overlappingBusinessRules: 0,
      matchingPlans: 0
    }

    this.matchingDataFields = [
      'line_of_coverage',
      'audience',
      'plan_metadata_field',
      'plan_metadata_value',
      'issuers',
      'private_exchange_id',
      'state_ids',
      'carrier_brands'
    ]
  }

  componentDidMount() {
    this.setState((prevState) => {
      const newValues = clone(prevState.values)
      forEach(newValues.matching_data, (value, key) => {
        if (this.matchingDataFields.includes(key)) {
          newValues[key] = value
        } else {
          newValues.plan_metadata_field = key
          newValues.plan_metadata_value = value
        }
      })

      return { values: newValues }
    })
  }

  handleChange = (field, value) => {
    this.setState((prevState) => {
      const newValues = clone(prevState.values)
      newValues[field] = value

      if (field === 'shared_business_rule_type' && value === this.props.initialValues.shared_business_rule_type) {
        newValues.payload = this.props.initialValues.payload
      } else if (field === 'shared_business_rule_type') {
        newValues.payload = typeToPayloadMap[newValues.shared_business_rule_type]
      }

      return { values: newValues }
    })
  }

  missingRequiredFields = () => {
    return isEmpty(this.state.values.shared_business_rule_type)
      || isEmpty(this.state.values.effective_date)
      || isEmpty(this.state.values.matching_data)
      || isEmpty(this.state.values.matching_data.line_of_coverage)
  }

  submitValues = () => {
    const newValues = clone(this.state.values)
    newValues.id = this.props.id
    this.matchingDataFields.forEach((key) => { delete newValues[key] })

    return newValues
  }

  handleValidation = async () => {
    this.setState({ validationInProgress: true })
    const newValues = this.submitValues()

    const response = await this.props.createValidation(newValues)
    const resourceErrors = get(response, 'resourceErrors', null)
    const errors = get(response, 'errors', null)

    if (isNil(resourceErrors) && isNil(errors)) {
      this.setState({ validationInProgress: false })
      this.setState({
        requestValidated: true,
        overlappingBusinessRules: get(response, 'responseAttributes.overlapping_business_rules', []),
        matchingPlans: get(response, 'responseAttributes.matching_plans', [])
      })
    }

    if (resourceErrors || errors) {
      this.setState({ validationInProgress: false })
      this.props.handleSubmit(newValues)
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault()

    this.props.handleSubmit(this.submitValues())
  }

  updateMatchingData = (field, value) => {
    this.setState((prevState) => {
      const newValues = clone(prevState.values)
      let matchingData

      if (field === 'plan_metadata_field' && newValues.plan_metadata_field) {
        newValues.plan_metadata_value = null
        delete newValues.matching_data[newValues.plan_metadata_field]
      }

      newValues[field] = value

      if (!field.includes('plan_metadata_')) {
        matchingData = { [field]: value }
      } else if (!isNil(newValues.plan_metadata_value)) {
        matchingData = { [newValues.plan_metadata_field]: newValues.plan_metadata_value }
      }

      newValues.matching_data = { ...newValues.matching_data, ...matchingData }

      return { values: newValues }
    })
  }

  isDisabled = () => {
    return this.state.values.matching_type !== 'plan_id'
  }

  showErrors = () => {
    return this.props.resourceErroredFields.map((field) => (
      <div className="howie_error" key={`error-${field}`}>
        {`${CapitalizeValueDisplay(field)} ${this.props.resourceErrors[field]}`}
      </div>
    ))
  }

  showOverlappingBusinessRulesLink = () => {
    let overlappingUrlPath = `/business_rules?search_term=effective_date: ${this.state.values.effective_date}`
      + ` | shared_business_rule_type: ${this.state.values.shared_business_rule_type}`
      + ` | matching_type: ${this.state.values.matching_type}`

    if (this.state.values.line_of_coverage) {
      overlappingUrlPath += ` | line_of_coverage: ${this.state.values.line_of_coverage}`
    }

    if (this.state.values.audience) {
      overlappingUrlPath += ` | audience: ${this.state.values.audience}`
    }

    return overlappingUrlPath
  }

  validationResults = () => {
    const { matchingPlans, overlappingBusinessRules } = this.state

    return (
      <div className="flash-message-container">
        <p>{`${matchingPlans} affected ${Pluralize('plan', matchingPlans)}.`}</p>
        <p>
          {`${overlappingBusinessRules} overlapping ${Pluralize('Business Rule', overlappingBusinessRules)}.`}
          <a
            href={this.showOverlappingBusinessRulesLink()}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </a>
        </p>
      </div>
    )
  }

  render() {
    const {
      resourceErroredFields,
      businessRuleTypes,
      matchingTypes,
      requestInProgress,
      submitLabel
    } = this.props

    return (
      <form className="pure-form pure-form-stacked" onSubmit={this.handleSubmit}>
        {resourceErroredFields.length > 0 && this.showErrors()}
        {this.state.requestValidated && this.validationResults()}

        <fieldset>
          <SelectField
            name="shared_business_rule_type"
            label="Business Rule Type *"
            options={businessRuleTypes}
            handleChange={this.handleChange}
            value={this.state.values.shared_business_rule_type}
          />

          <div id="payload">
            <label>
              <div>
                <span className="buffer-right">Business Rule Data</span>
              </div>

              <JsonField
                name="payload"
                rows={10}
                handleChange={this.handleChange}
                value={this.state.values.payload}
              />
            </label>
          </div>
        </fieldset>

        <div className="flex-box flex-box--align-start">
          <div className="flex-box__item">
            <LabeledTextField
              label="Effective date *"
              name="effective_date"
              key="effective_date"
              placeholder="YYYY-MM-DD"
              type="date"
              handleChange={this.handleChange}
              value={this.state.values.effective_date}
            />

            <LabeledTextField
              label="Expiration date"
              name="expiration_date"
              key="expiration_date"
              placeholder="YYYY-MM-DD"
              type="date"
              handleChange={this.handleChange}
              value={this.state.values.expiration_date}
            />

            <MatchingDataFields
              matchingTypes={matchingTypes}
              values={this.state.values}
              matchingDataFields={this.matchingDataFields}
              handleChange={this.handleChange}
              updateMatchingData={this.updateMatchingData}
            />
          </div>

          <div className="flex-box__item">
            <fieldset>
              <LabeledJsonField
                name="matching_data"
                label="Matching Data *"
                rows={22}
                handleChange={this.handleChange}
                value={this.state.values.matching_data}
                disabled={this.isDisabled()}
              />
            </fieldset>
          </div>
        </div>

        <fieldset>
          <SubmitButton
            title="Submit"
            className="pure-button pure-button-primary"
            text={submitLabel}
            disabled={!this.state.requestValidated || this.missingRequiredFields()}
            loading={requestInProgress}
          />

          <button
            className="pure-button pure-button-secondary validate-button"
            type="button"
            onClick={this.handleValidation}
            disabled={this.missingRequiredFields() || this.state.validationInProgress}
          >
            Validate
          </button>
        </fieldset>
      </form>
    )
  }
}
